<!--
  @component EnvSecretsPanel — per-project environment + secrets + bindings.
  tier:      composed panel (shared by consumer Settings drawer + admin drill-down)
  reads:     useDeploymentIO → projects.config.* (get/setEnv/unsetEnv/
             setSecret/unsetSecret/unsetBinding)
  purpose:   the "manage what your deployed app runs with" surface. Env vars
             (plaintext), secrets (reveal-once on entry, masked forever after —
             values never leave the server), and auto-managed substrate
             bindings (DATABASE_URL ← your database). release.run injects all of
             it at boot.

  Honest model: secret values are write-only — the panel never reads a value
  back. Reserved names (DATABASE_URL, REDIS_URL, QUEUE_URL, R2_*) are managed by
  substrate bindings and refused as manual entries (server enforces; surfaced here).
-->
<script lang="ts">
  import { useDeploymentIO } from "../io/nodes/deployment/DeploymentIO.svelte";

  interface Props { projectId: string; }
  const { projectId }: Props = $props();

  const node = useDeploymentIO();

  interface Binding { name: string; kind: string; resourceId: string; }
  let env = $state<Record<string, string>>({});
  let secretNames = $state<readonly string[]>([]);
  let bindings = $state<readonly Binding[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  // Draft inputs.
  let newEnvName = $state("");
  let newEnvValue = $state("");
  let newSecretName = $state("");
  let newSecretValue = $state("");
  let busy = $state(false);

  async function load(): Promise<void> {
    if (!projectId) return;
    loading = true; error = null;
    try {
      const r = (await node.dispatch("projects.config.get", { projectId })) as {
        env?: Record<string, string>;
        secrets?: { name: string }[];
        bindings?: Binding[];
      };
      env = r?.env ?? {};
      secretNames = (r?.secrets ?? []).map((s) => s.name);
      bindings = r?.bindings ?? [];
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  $effect(() => { void load(); });

  async function run(op: string, payload: Record<string, unknown>): Promise<void> {
    busy = true; error = null;
    try { await node.dispatch(op, { projectId, ...payload }); await load(); }
    catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { busy = false; }
  }

  async function addEnv(): Promise<void> {
    const name = newEnvName.trim();
    if (!name) return;
    await run("projects.config.setEnv", { name, value: newEnvValue });
    if (!error) { newEnvName = ""; newEnvValue = ""; }
  }
  async function addSecret(): Promise<void> {
    const name = newSecretName.trim();
    if (!name || !newSecretValue) return;
    await run("projects.config.setSecret", { name, value: newSecretValue });
    if (!error) { newSecretName = ""; newSecretValue = ""; }
  }

  const envEntries = $derived(Object.entries(env).sort((a, b) => a[0].localeCompare(b[0])));
  function bindingLabel(b: Binding): string {
    return `${b.name} ← ${b.kind} (${b.resourceId})`;
  }
</script>

<div class="cfg">
  {#if error}<p class="err">{error}</p>{/if}

  <!-- Environment variables -->
  <section class="block">
    <p class="block-label">Environment variables</p>
    {#if loading}
      <p class="muted">Loading…</p>
    {:else if envEntries.length === 0}
      <p class="muted">No environment variables yet.</p>
    {:else}
      <ul class="rows">
        {#each envEntries as [k, v] (k)}
          <li class="row">
            <code class="k">{k}</code>
            <code class="v">{v}</code>
            <button type="button" class="x" title="Remove" disabled={busy} onclick={() => run("projects.config.unsetEnv", { name: k })}>✕</button>
          </li>
        {/each}
      </ul>
    {/if}
    <div class="add">
      <input type="text" placeholder="NAME" bind:value={newEnvName} spellcheck="false" />
      <input type="text" placeholder="value" bind:value={newEnvValue} spellcheck="false" />
      <button type="button" class="addbtn" disabled={busy || !newEnvName.trim()} onclick={() => void addEnv()}>Add</button>
    </div>
  </section>

  <!-- Secrets -->
  <section class="block">
    <p class="block-label">Secrets</p>
    <p class="hint">Encrypted at rest. The value is shown only as you type it — never readable again.</p>
    {#if !loading && secretNames.length > 0}
      <ul class="rows">
        {#each secretNames as name (name)}
          <li class="row">
            <code class="k">{name}</code>
            <span class="masked">••••••••</span>
            <button type="button" class="x" title="Remove" disabled={busy} onclick={() => run("projects.config.unsetSecret", { name })}>✕</button>
          </li>
        {/each}
      </ul>
    {:else if !loading}
      <p class="muted">No secrets yet.</p>
    {/if}
    <div class="add">
      <input type="text" placeholder="NAME" bind:value={newSecretName} spellcheck="false" />
      <input type="password" placeholder="value" bind:value={newSecretValue} spellcheck="false" autocomplete="off" />
      <button type="button" class="addbtn" disabled={busy || !newSecretName.trim() || !newSecretValue} onclick={() => void addSecret()}>Add</button>
    </div>
  </section>

  <!-- Substrate bindings -->
  <section class="block">
    <p class="block-label">Connected resources</p>
    {#if !loading && bindings.length > 0}
      <ul class="rows">
        {#each bindings as b (b.name)}
          <li class="row">
            <code class="k">{b.name}</code>
            <span class="bind">{bindingLabel(b)}</span>
            <button type="button" class="x" title="Unbind" disabled={busy} onclick={() => run("projects.config.unsetBinding", { name: b.name })}>✕</button>
          </li>
        {/each}
      </ul>
    {:else if !loading}
      <p class="muted">No connected resources. Connect a database to auto-bind <code>DATABASE_URL</code>.</p>
    {/if}
  </section>
</div>

<style>
  .cfg { display: flex; flex-direction: column; gap: var(--space-4); font-family: var(--type-font); }
  .block { display: flex; flex-direction: column; gap: var(--space-2); }
  .block-label { margin: 0; font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-tertiary); }
  .hint { margin: 0; font-size: 11.5px; color: var(--text-tertiary); line-height: 1.5; }
  .muted { margin: 0; font-size: 12.5px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12px; color: var(--error); }

  .rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .row { display: grid; grid-template-columns: minmax(90px, auto) 1fr auto; gap: var(--space-3); align-items: center; padding: 6px 0; border-top: 0.5px solid var(--border-subtle); }
  .row:first-child { border-top: 0; }
  .k { font-family: var(--type-mono); font-size: 11.5px; color: var(--text-primary); white-space: nowrap; }
  .v { font-family: var(--type-mono); font-size: 11.5px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .masked { font-size: 12px; color: var(--text-ghost); letter-spacing: 2px; }
  .bind { font-size: 12px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .x { appearance: none; background: transparent; border: 0; color: var(--text-ghost); font-size: 11px; cursor: pointer; padding: 2px 6px; border-radius: var(--radius-sm); }
  .x:hover:not(:disabled) { color: var(--error); background: var(--bg-hover); }
  .x:disabled { opacity: 0.4; cursor: default; }

  .add { display: flex; gap: var(--space-2); align-items: center; margin-top: var(--space-1); }
  .add input { flex: 1; min-width: 0; padding: 5px var(--space-2); background: var(--surface-sunken); border: 0.5px solid var(--border); border-radius: var(--radius-sm); color: var(--text-primary); font: inherit; font-size: 12px; font-family: var(--type-mono); }
  .add input:focus { outline: none; border-color: var(--border-focus); }
  .addbtn { appearance: none; background: var(--bg-active); border: 0.5px solid var(--border-strong); border-radius: var(--radius-sm); color: var(--text-primary); font: inherit; font-size: 12px; padding: 5px var(--space-3); cursor: pointer; white-space: nowrap; }
  .addbtn:hover:not(:disabled) { background: var(--bg-selected); }
  .addbtn:disabled { opacity: 0.45; cursor: default; }
</style>
